import * as React from 'react';
import { I18nContextProvider } from 'i18n/I18nProvider';
import Toast from 'components/Toast';
import toastNotification, { claimToastRendering } from 'state/toast';
import { ISessionData } from 'data/schema';
import GlobalConfirmDialog from 'components/ConfirmDialog/GlobalConfirmDialog';

export type TWebComponentProps = {
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | React.ReactNode
    | object
    | HTMLElement
    | ShadowRoot
    | ((...args: unknown[]) => unknown);
};

export interface IWebComponentContext extends TWebComponentProps {
  hasSlot?: boolean;
  rootElement?: ShadowRoot;
  webComponent?: HTMLElement & TWebComponentProps;
}

export interface IWebComponentRootProps extends Partial<ISessionData>, IWebComponentContext {
  children: React.ReactNode;
  /**
   * Whether this root may claim toast rendering. Defaults to true. The runtime-owned dialog host
   * (AVS-4404) passes false: it exists only to guarantee a visible confirm-dialog claimant, and a
   * toast rendered inside a body-appended shadow root would sit outside the integrator's layout
   * and be unreachable by their `::part` selectors.
   */
  claimsToasts?: boolean;
  /** Forwarded to GlobalConfirmDialog as `claimDelayMs`. Only the runtime-owned dialog host sets it. */
  dialogClaimDelayMs?: number;
}

const WebComponentContext = React.createContext<IWebComponentContext | undefined>(undefined);

export function WebComponentRoot({
  children,
  hasSlot,
  claimsToasts = true,
  dialogClaimDelayMs,
  ...props
}: IWebComponentRootProps) {
  const toastState = toastNotification.useState();
  const [shouldRenderToast, setShouldRenderToast] = React.useState(false);

  // Check if the web component is visible in the DOM
  const isWebComponentVisible = React.useCallback(() => {
    const webComponent = props.webComponent || props.rootElement?.host;
    if (!webComponent) return true; // If no reference, assume visible

    // A host removed from the document (e.g. an Angular integrator's *ngIf destroying
    // <e3d-sharing-dashboard>) is not visible, even though its computed style says otherwise:
    // getComputedStyle() on a detached element returns empty strings for every property, so the
    // CSS checks below would all pass and this zombie root would keep claiming toasts/dialogs it
    // can never show. wc() does unmount on detach (utils/web-component.tsx), but only one microtask
    // later and only if the host is still detached by then, so a synchronous caller in that window
    // still sees a live tree. isConnected is what distinguishes a live host from a detached one.
    if (!(webComponent as Element).isConnected) {
      return false;
    }

    // AVS-4404: checkVisibility() walks the ancestor chain. getComputedStyle(el).display reports
    // the element's OWN display, so a host under a display:none ancestor used to pass every check
    // below and claim a dialog it could never paint. A claimed dialog has no settle timer
    // (confirm-dialog.ts), so that claim hangs forever and share() never resolves.
    const element = webComponent as Element;
    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({
        opacityProperty: true,
        visibilityProperty: true,
        contentVisibilityAuto: true,
      });
    }

    // Fallback for browsers without checkVisibility (Safari < 17.4): own-element CSS only.
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    return true;
  }, [props.webComponent, props.rootElement]);

  // Only decide to render toast once when toasts are added and not being rendered
  React.useEffect(() => {
    if (toastState.toasts.length > 0 && !toastState.isBeingRendered) {
      // Only claim toast rendering if this web component is actually visible
      if (claimsToasts && isWebComponentVisible() && claimToastRendering()) {
        setShouldRenderToast(true);
      }
    } else if (toastState.toasts.length === 0) {
      setShouldRenderToast(false);
    }
  }, [toastState.toasts.length, toastState.isBeingRendered, isWebComponentVisible, claimsToasts]);

  // Observe visibility changes when rendering toast to release claim if component becomes hidden
  React.useEffect(() => {
    if (!shouldRenderToast) return;

    const webComponent = props.webComponent || props.rootElement?.host;
    if (!webComponent) return;

    const checkVisibility = () => {
      if (!isWebComponentVisible()) {
        setShouldRenderToast(false);
        toastNotification.setValues({ isBeingRendered: false });
      }
    };

    // Observe style and attribute changes (display, visibility, opacity, etc.)
    const mutationObserver = new MutationObserver(checkVisibility);
    mutationObserver.observe(webComponent as Element, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [shouldRenderToast, isWebComponentVisible, props.webComponent, props.rootElement]);

  return (
    <WebComponentContext.Provider value={{ hasSlot, ...props }}>
      <I18nContextProvider>
        {shouldRenderToast && <Toast />}
        <GlobalConfirmDialog
          isHostVisible={isWebComponentVisible}
          hostTagName={(props.webComponent || props.rootElement?.host)?.tagName?.toLowerCase()}
          claimDelayMs={dialogClaimDelayMs}
        />
        {children}
      </I18nContextProvider>
    </WebComponentContext.Provider>
  );
}

export default WebComponentRoot;

export const useWebComponent = (): IWebComponentContext => {
  const context = React.useContext(WebComponentContext);
  if (context === undefined) {
    // hook used without the WebComponentRoot parent
    return { hasSlot: false };
  }
  return context;
};
